import React from 'react';
import {
    ContactDetails,
    InsuranceCareTeamMetadata,
    ProviderCareTeamMetadata,
    SupportCareTeamMetadata,
} from '../../../primary-record-types/DynamoTypes';
import { getIntl } from '../../../utils/intl';
import genderSexToString from '../../../utils/genderSexToString';
import addressToString from '../../../utils/addressToString';
import LabelValueDisplay, { LabelValueItem } from '../../v2BaseComponents/LabelValueDisplay';
import getAge from '../../../utils/getAge';
import getName from '../../../utils/getName';
import { OrgOrProfileCareTeamMember, isSupport } from '../../../utils/careTeamUtils';
import TitledSection from '../../v2BaseComponents/TitledSection';
import { supportTypeTranslations } from '../CareTeamForm/utils';

const intl = getIntl('careTeamMember.detailsList', {
    dob: 'Date of Birth',
    gender: 'Gender (Sex assigned at birth)',
    address: 'Address',
    phoneNumber: 'Phone Number',
    relationship: 'Relationship',
    description: 'Description',
    faxNumber: 'Fax Number',
    url: 'URL',
    role: 'Role',
    email: 'Email',
    insuranceProviderName: 'Insurance Provider Name',
    primaryCarrier: 'Primary Carrier',
    insuranceRole: 'Insurance Role',
    groupNumber: 'Group Number',
    planNumber: 'Plan Number',
    memberId: 'Member ID',
    expirationDate: 'Expiration Date',
    isPreferred: 'Is Preferred',
    isActive: 'Is Active',
    providerType: 'Provider Type',
    providerSpecialty: 'Provider Specialty',
    yes: 'Yes',
    no: 'No',
    company: 'Company',
    supportType: 'Support Type',
    details: 'Details',
    ...supportTypeTranslations,
});

export type DetailsListOwnProps = {
    careTeamMember: OrgOrProfileCareTeamMember;
    noTitle?: boolean;
};

export type DetailsListProps = DetailsListOwnProps;

const getInsuranceMetadataLabelValues = (metadata: InsuranceCareTeamMetadata): LabelValueItem[] => [
    {
        label: intl('insuranceProviderName'),
        value: metadata.insuranceProviderName || '',
    },
    {
        label: intl('primaryCarrier'),
        value: getName({
            firstName: metadata.primaryCarrierFirstName,
            lastName: metadata.primaryCarrierLastName,
        }),
    },
    {
        label: intl('insuranceRole'),
        value: metadata.insuranceRole || '',
    },
    {
        label: intl('groupNumber'),
        value: metadata.groupNumber || '',
    },
    {
        label: intl('planNumber'),
        value: metadata.planNumber || '',
    },
    {
        label: intl('memberId'),
        value: metadata.memberId || '',
    },
    {
        label: intl('expirationDate'),
        value: metadata.expirationDate || '',
    },
    {
        label: intl('isPreferred'),
        value: metadata.isPreferred ? intl('yes') : intl('no'),
    },
    {
        label: intl('isActive'),
        value: metadata.isActive ? intl('yes') : intl('no'),
    },
];

const getProviderMetadataLabelValues = (metadata: ProviderCareTeamMetadata): LabelValueItem[] => [
    {
        label: intl('providerType'),
        value: metadata.providerType || '',
    },
    {
        label: intl('providerSpecialty'),
        value: metadata.providerSpecialty || '',
    },
    {
        label: intl('isActive'),
        value: metadata.isActive ? intl('yes') : intl('no'),
    },
];

const getSupportMetadataLabelValues = (metadata: SupportCareTeamMetadata): LabelValueItem[] => {
    const otherSupportType = metadata.otherTypeDescription ? `: ${metadata.otherTypeDescription}` : '';
    const supportTypeIncludingOther = metadata.supportCareTeamMemberType
        ? ((intl as any)(metadata.supportCareTeamMemberType) || metadata.supportCareTeamMemberType) + otherSupportType
        : '';

    return [
        {
            label: intl('supportType'),
            value: supportTypeIncludingOther,
        },
        {
            label: intl('isActive'),
            value: metadata.isActive ? intl('yes') : intl('no'),
        },
    ];
};

const DetailsList: React.FC<DetailsListProps> = ({ careTeamMember, noTitle }) => {
    let metadataLabelValues: LabelValueItem[] = [];
    if (isSupport(careTeamMember)) {
        metadataLabelValues = getSupportMetadataLabelValues(careTeamMember.metadata);
    } else if (careTeamMember.careTeamMemberType === 'insurance') {
        metadataLabelValues = getInsuranceMetadataLabelValues(careTeamMember.metadata as InsuranceCareTeamMetadata);
    } else if (careTeamMember.careTeamMemberType === 'provider') {
        metadataLabelValues = getProviderMetadataLabelValues(careTeamMember.metadata as ProviderCareTeamMetadata);
    }

    const getContactLabelValues = (): LabelValueItem[] => {
        return [
            {
                label: intl('company'),
                value: (careTeamMember.contactDetails as ContactDetails).company || '',
            },
            {
                label: intl('dob'),
                value: (careTeamMember.contactDetails as ContactDetails).dob
                    ? `${(careTeamMember.contactDetails as ContactDetails).dob} (${getAge(
                          (careTeamMember.contactDetails as ContactDetails).dob,
                      )})`
                    : '',
            },
            {
                label: intl('gender'),
                value: `${genderSexToString((careTeamMember.contactDetails as ContactDetails).gender) || ''}`,
            },
            {
                label: intl('relationship'),
                value: (careTeamMember.contactDetails as ContactDetails).relationship || '',
            },
            {
                label: intl('role'),
                value: (careTeamMember.contactDetails as ContactDetails).role || '',
            },
            {
                label: intl('phoneNumber'),
                value: (careTeamMember.contactDetails as ContactDetails).phoneNumber || '',
                linkType: 'phone',
            },
            {
                label: intl('email'),
                value: (careTeamMember.contactDetails as ContactDetails).email || '',
                linkType: 'email',
            },
            {
                label: intl('faxNumber'),
                value: (careTeamMember.contactDetails as ContactDetails).faxNumber || '',
            },
            {
                label: intl('url'),
                value: (careTeamMember.contactDetails as ContactDetails).url || '',
                linkType: 'url',
            },
            {
                label: intl('address'),
                value: addressToString({
                    address1: (careTeamMember.contactDetails as ContactDetails).address1,
                    address2: (careTeamMember.contactDetails as ContactDetails).address2,
                    city: (careTeamMember.contactDetails as ContactDetails).city,
                    state: (careTeamMember.contactDetails as ContactDetails).state,
                    zipCode: (careTeamMember.contactDetails as ContactDetails).zipCode,
                }),
                linkType: 'address',
            },
            {
                label: intl('description'),
                value: (careTeamMember.contactDetails as ContactDetails).description || '',
            },
        ];
    };

    const content = <LabelValueDisplay items={[...metadataLabelValues, ...getContactLabelValues()]} omitEmptyValues />;

    return noTitle ? content : <TitledSection title={intl('details')}>{content}</TitledSection>;
};

export default React.memo(DetailsList);
